import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import instance from '../../../../helpers/axiosInstance';
import { commonServiceMethod } from '../../../../helpers/helper';
import Loader from '../../../global/loader';
import SuccessToast from '../../../customToast/SuccessToast';
import ErrorToast from '../../../customToast/ErrorToast';
import { Slide, toast } from 'react-toastify';
import Calandar from '../../../../assets/Icondate.svg';

// Hard server cap mirrors LabelExportController.MAX_RANGE_DAYS.
const MAX_RANGE_DAYS = 31;

// Polling cadence while any job is queued/running. Kept deliberately slow so
// a user who leaves the page open doesn't hammer the backend — 5s is roughly
// "fast enough to feel live, slow enough not to matter".
const POLL_INTERVAL_MS = 5000;

const LabelsByDateBody = () => {
    const [loader, setLoader] = useState(false);

    // Submission form state. No status filter in the UI — the server defaults
    // to "every order with a label", i.e. everything except unlabel / cancel /
    // refunded. This matches the warehouse's ask: give me all labels in the
    // window, regardless of where the order has since moved in the lifecycle.
    const [startDate, setStartDate] = useState(moment().startOf('month').toDate());
    const [endDate, setEndDate] = useState(moment().toDate());

    // Job list state.
    const [jobs, setJobs] = useState([]);
    const pollTimerRef = useRef(null);

    // -----------------------------------------------------------------
    // Initial load + polling
    // -----------------------------------------------------------------

    useEffect(() => {
        fetchJobs();
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Whenever the job list changes, decide whether we still need to poll.
    // If any row is queued or running, schedule another fetch. Otherwise stop
    // — nothing is going to change on its own.
    useEffect(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        const hasLive = jobs.some((j) => j.state === 'queued' || j.state === 'running');
        if (hasLive) {
            pollTimerRef.current = setTimeout(fetchJobs, POLL_INTERVAL_MS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs]);

    const fetchJobs = () => {
        // Intentionally not flipping the page-level loader on poll — we only
        // show the spinner for user-initiated actions. Silent refreshes
        // shouldn't flash the UI.
        commonServiceMethod('app/labels/exports?limit=20', 'get', null, {}, () => {}, (res) => {
            if (res?.data?.jobs) setJobs(res.data.jobs);
        });
    };

    // -----------------------------------------------------------------
    // Submission
    // -----------------------------------------------------------------

    const rangeInDays = () =>
        moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days') + 1;

    const submitExport = () => {
        if (rangeInDays() > MAX_RANGE_DAYS) {
            toast(<ErrorToast body={`Date range is limited to ${MAX_RANGE_DAYS} days`} />, {
                transition: Slide, hideProgressBar: true, autoClose: 3000,
            });
            return;
        }

        // status_filter is deliberately omitted — the backend default covers
        // every order that has a label (excludes only unlabel, cancel, refunded).
        const body = {
            from_date: moment(startDate).format('YYYY-MM-DD'),
            to_date: moment(endDate).format('YYYY-MM-DD'),
        };

        commonServiceMethod('app/labels/export', 'post', body, {}, setLoader, (res) => {
            if (res?.data?.status_code === 200) {
                toast(
                    <SuccessToast body={`Export queued — ${res.data.total_orders} orders`} />,
                    { transition: Slide, hideProgressBar: true, autoClose: 3000 }
                );
                fetchJobs();
            } else {
                toast(<ErrorToast body={res?.data?.message || 'Failed to queue export'} />, {
                    transition: Slide, hideProgressBar: true, autoClose: 4000,
                });
            }
        }, (err) => {
            const msg = err?.response?.data?.message || 'Failed to queue export';
            toast(<ErrorToast body={msg} />, {
                transition: Slide, hideProgressBar: true, autoClose: 4000,
            });
        });
    };

    // -----------------------------------------------------------------
    // Download — proxy endpoint returns an application/zip blob
    // -----------------------------------------------------------------

    const downloadJob = async (job) => {
        // Use the axios instance directly so we can request the blob type;
        // commonServiceMethod is fine for JSON payloads but doesn't carry
        // the config through as cleanly for binary downloads.
        setLoader(true);
        try {
            const res = await instance({
                url: `app/labels/export/${job.job_uuid}/download`,
                method: 'get',
                responseType: 'blob',
            });
            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `labels_${job.from_date}_to_${job.to_date}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast(<ErrorToast body="Download failed — the export may have expired" />, {
                transition: Slide, hideProgressBar: true, autoClose: 4000,
            });
            // Refresh the list so an expired row's button disappears.
            fetchJobs();
        } finally {
            setLoader(false);
        }
    };

    // -----------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------

    const progressLabel = (j) => {
        const total = j.total_orders || 0;
        const processed = j.processed || 0;
        const failed = j.failed || 0;
        if (j.state === 'queued')    return `Queued (${total} orders)`;
        if (j.state === 'running')   return `${processed + failed} / ${total}${failed ? ` (${failed} failed)` : ''}`;
        if (j.state === 'completed') return `${processed} / ${total}${failed ? ` (${failed} skipped)` : ''}`;
        if (j.state === 'failed')    return j.error_message || 'Failed';
        if (j.state === 'expired')   return 'Expired — re-run to download again';
        return j.state;
    };

    const sizeLabel = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Display helper: the backend now returns created_at / updated_at /
    // expires_at already formatted as "YYYY-MM-DD HH:mm:ss" in IST. We still
    // reformat for the UI style, but we do NOT re-apply a timezone — the
    // string has no TZ suffix, so moment() treats it as local. That's fine
    // because the backend already shifted it to Asia/Kolkata.
    const formatIst = (s) => {
        if (!s) return '—';
        const m = moment(s, 'YYYY-MM-DD HH:mm:ss');
        return m.isValid() ? m.format('DD MMM YYYY HH:mm') : s;
    };

    const formatIstDate = (s) => {
        if (!s) return '—';
        const m = moment(s, 'YYYY-MM-DD HH:mm:ss');
        return m.isValid() ? m.format('DD MMM YYYY') : s;
    };

    // -----------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------
    //
    // Layout note: the outer <OrdersTabMenu> already wraps us in
    // `.orders-filter-page.page-container.pos-relative`, which applies the
    // page-level 40px padding. We purposely DO NOT repeat those classes here
    // — nesting them would double the padding and shrink the content width,
    // which is what the UAT layout/overlap reports were hitting. A plain
    // fragment keeps the content flush with the rest of the app.

    return (
        <>
            {loader && <Loader />}

            {/* --- Form card --- */}
            <div className="card p-3 mb-3">
                <h5 className="fw-600 mb-3">Download Labels by Date Range</h5>

                <div className="d-flex flex-wrap align-items-end" style={{ gap: '16px' }}>
                    <div>
                        <label className="form-label small fw-600">Date range</label>
                        <DateRangePicker
                            initialSettings={{
                                startDate,
                                endDate,
                                maxSpan: { days: MAX_RANGE_DAYS - 1 },
                                locale: { format: 'DD MMM YYYY' },
                            }}
                            onCallback={(start, end) => {
                                setStartDate(start.toDate());
                                setEndDate(end.toDate());
                            }}
                        >
                            <div className="d-flex align-items-center border rounded px-2 py-1"
                                 style={{ cursor: 'pointer', minWidth: '240px' }}>
                                <img src={Calandar} alt="date" style={{ width: 16, marginRight: 8 }} />
                                <span>
                                    {moment(startDate).format('DD MMM YYYY')} – {moment(endDate).format('DD MMM YYYY')}
                                </span>
                            </div>
                        </DateRangePicker>
                    </div>

                    <div>
                        <button className="btn btn-primary fw-600" onClick={submitExport}>
                            Start Export
                        </button>
                    </div>
                </div>
            </div>

            {/* --- Jobs table --- */}
            <div className="card p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-600 mb-0">Recent exports</h6>
                    <button className="btn btn-link p-0" onClick={fetchJobs}>Refresh</button>
                </div>
                {jobs.length === 0 ? (
                    <p className="text-muted mb-0">No exports yet — pick a date range above to create one.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>Requested (IST)</th>
                                    <th>Range</th>
                                    <th>State</th>
                                    <th>Progress</th>
                                    <th>Size</th>
                                    <th>Expires (IST)</th>
                                    <th style={{ width: 140 }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map((j) => (
                                    <tr key={j.job_uuid}>
                                        <td>{formatIst(j.created_at)}</td>
                                        <td>{j.from_date} → {j.to_date}</td>
                                        <td>
                                            <span className={`badge bg-${stateColor(j.state)}`}>{j.state}</span>
                                        </td>
                                        <td>{progressLabel(j)}</td>
                                        <td>{sizeLabel(j.zip_size_bytes)}</td>
                                        <td>{formatIstDate(j.expires_at)}</td>
                                        <td>
                                            {j.downloadable ? (
                                                <button className="btn btn-sm btn-outline-primary fw-600"
                                                        onClick={() => downloadJob(j)}>
                                                    Download ZIP
                                                </button>
                                            ) : (
                                                <span className="text-muted small">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

// Colour token for the state badge — maps to whatever Bootstrap theme is in
// use. Kept as a plain helper so there's no surprise styling imported here.
const stateColor = (state) => {
    switch (state) {
        case 'completed': return 'success';
        case 'running':   return 'info';
        case 'queued':    return 'secondary';
        case 'failed':    return 'danger';
        case 'expired':   return 'warning';
        default:          return 'secondary';
    }
};

export default LabelsByDateBody;
